import React from 'react'
import { FormProvider } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { ModalBase, IModalBaseProps, MODAL_TYPES } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { ICarrierState } from 'types'
import { getCarrierChecklist } from 'utils/actions/carrier'
import { IStore } from 'apps/carrier/utils/reducers'
import { useDeprecatedBrokerCarrierAgreementsQuery } from '../../../Documents/DeprecatedBrokerCarrierAgreements'
import { useCarrierStateQuery } from '../graphql/CarrierState'
import { useCarrierStatusQuery } from '../graphql/CarrierStatus'
import { useCreateRulesAssessmentOverridesMutation } from '../graphql/CreateRulesAssessmentOverrides'
import { IRulesAssessmentFragment } from '../graphql/RulesAssessmentFragment'
import { useTransfixRulesAssessmentQuery } from '../graphql/TransfixRulesAssessment'
import { RulesManualReviewModalForm } from './RulesManualReviewModalForm'
import { TRulesManualReviewModalSchema } from './RulesManualReviewModalSchema'
import { TRules } from './types'
import { useRulesManualReviewModalForm } from './useRulesManualReviewModalForm'
import { getOverrideInput } from './utils'

interface IRulesManualReviewModalProps extends Omit<IModalBaseProps, 'modalType'> {
  allRules: IRulesAssessmentFragment[]
  rules: TRules
}

export const RulesManualReviewModal = ({
  allRules,
  onClose,
  open = false,
  rules,
  ...props
}: IRulesManualReviewModalProps) => {
  const { openToast } = useGlobalToast()
  const { carrierDetails } = useSelector<IStore, ICarrierState>(({ carrier }) => carrier)
  const { id: carrierApplicationId = '' } = carrierDetails || {}
  const [createRulesOverride, { loading }] = useCreateRulesAssessmentOverridesMutation()
  const { refetch: carrierStateRefetch } = useCarrierStateQuery({
    variables: { carrierApplicationId },
  })
  const { refetch: highwayRulesRefetch } = useCarrierStatusQuery({
    variables: { carrierApplicationId },
  })
  const { refetch: transfixRulesRefetch } = useTransfixRulesAssessmentQuery({
    variables: { carrierApplicationId },
  })
  const {
    refetch: deprecatedBrokerCarrierAgreementsRefetch,
  } = useDeprecatedBrokerCarrierAgreementsQuery({
    variables: {
      id: carrierApplicationId,
    },
  })

  const methods = useRulesManualReviewModalForm({
    defaultValues: {},
  })

  const { handleSubmit } = methods || {}
  const dispatch = useDispatch()

  const submitHandler = async (data: TRulesManualReviewModalSchema) => {
    const { decision, rules } = data || {}

    const rulesAssessmentOverrides =
      rules.map(rule => getOverrideInput(rule, decision, allRules)) || []

    const { data: response, errors } = await createRulesOverride({
      variables: {
        input: {
          carrierApplicationId,
          rulesAssessmentOverrides,
        },
      },
    })

    const { createRulesAssessmentOverrides } = response || {}
    const { errors: responseErrors } = createRulesAssessmentOverrides || {}
    const allErrors = [...(errors || []), ...(responseErrors || [])]

    carrierStateRefetch()
    highwayRulesRefetch()
    transfixRulesRefetch()

    const bcaRule = rules.find(rule => rule === 'BCA')
    if (bcaRule && decision === 'incomplete') {
      dispatch(getCarrierChecklist(carrierApplicationId))
      deprecatedBrokerCarrierAgreementsRefetch()
    }

    if (allErrors.length) {
      return openToast({
        toastMessage:
          allErrors[0].message
            .replace(/([a-z]+_[a-z]+)+/g, '')
            .replace(':', '')
            .trim() || 'Something went wrong',
        toastType: 'alert',
      })
    }

    return onClose()
  }

  const { decision: selectedDecision, rules: selectedRules } = methods?.watch() || {}
  const hasSelectedADecisionAndRules = !!selectedDecision && !!selectedRules?.length
  const actions: IModalBaseProps['actions'] = [
    {
      label: 'Cancel',
      action: onClose,
      type: 'dismiss',
    },
    {
      label: 'Submit',
      action: handleSubmit(submitHandler),
      disabled: !hasSelectedADecisionAndRules || loading,
      type: 'confirm',
    },
  ]

  return (
    <ModalBase
      dataTest='RulesManualReviewModal'
      title='Submit Decision'
      modalType={MODAL_TYPES.form}
      size={6}
      open={open}
      actions={actions}
      onClose={onClose}
      {...props}>
      <FormProvider {...methods}>
        <RulesManualReviewModalForm methods={methods} rules={rules} />
      </FormProvider>
    </ModalBase>
  )
}
